import React, { useState, useEffect, useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { X, ChevronDown, Search, Loader2, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { fetchAllergies, AllergyOption } from '@/services/allergyService';
import { Button } from './button';

interface AllergyMultiSelectProps {
  form?: UseFormReturn<any>;
  name?: string;
  value?: string[];
  onChange?: (next: string[]) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  dark?: boolean;
}

const AllergyMultiSelect: React.FC<AllergyMultiSelectProps> = ({
  form,
  name,
  value,
  onChange,
  label,
  placeholder = 'Search and select allergies...',
  required = false,
  disabled = false,
  className,
  dark = false,
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [allergyOptions, setAllergyOptions] = useState<AllergyOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);

  const isControlled = onChange !== undefined;
  const selectedAllergies: string[] = isControlled
    ? value || []
    : (form && name ? form.watch(name) : []) || [];

  const writeValue = (next: string[]) => {
    if (isControlled) {
      onChange!(next);
    } else if (form && name) {
      form.setValue(name, next, { shouldValidate: true });
    }
  };

  // Debounced search for allergies
  useEffect(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(async () => {
      if (searchTerm.trim().length >= 2) {
        setIsLoading(true);
        try {
          const results = await fetchAllergies(searchTerm.trim());
          setAllergyOptions(results);
        } catch (error) {
          console.error('Error fetching allergies:', error);
          setAllergyOptions([]);
        } finally {
          setIsLoading(false);
        }
      } else if (searchTerm.trim().length === 0) {
        // Load initial options when search is cleared
        setIsLoading(true);
        try {
          const results = await fetchAllergies('');
          setAllergyOptions(results.slice(0, 50)); // Limit initial results
        } catch (error) {
          console.error('Error fetching initial allergies:', error);
          setAllergyOptions([]);
        } finally {
          setIsLoading(false);
        }
      }
    }, 300);

    setSearchTimeout(timeout);

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [searchTerm]);

  // Load initial options when popover opens
  useEffect(() => {
    if (open && allergyOptions.length === 0 && !isLoading) {
      setIsLoading(true);
      fetchAllergies('')
        .then(results => {
          setAllergyOptions(results.slice(0, 50));
        })
        .catch(error => {
          console.error('Error fetching initial allergies:', error);
          setAllergyOptions([]);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [open]);

  const toggleAllergy = (allergyValue: string) => {
    const current = selectedAllergies || [];
    if (current.includes(allergyValue)) {
      writeValue(current.filter((item: string) => item !== allergyValue));
    } else {
      writeValue([...current, allergyValue]);
    }
  };

  const removeAllergy = (allergyValue: string) => {
    const current = selectedAllergies || [];
    writeValue(current.filter((item: string) => item !== allergyValue));
  };

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) {
      return allergyOptions;
    }
    const term = searchTerm.toLowerCase();
    return allergyOptions.filter(
      option =>
        option.label.toLowerCase().includes(term) || option.value.toLowerCase().includes(term)
    );
  }, [allergyOptions, searchTerm]);

  return (
    <div className={cn('space-y-2', className)}>
      {label && <Label className={required ? 'required' : ''}>{label}</Label>}

      {/* Trigger Button */}
      <button
        type='button'
        disabled={disabled}
        onClick={() => setOpen(true)}
        style={{ minHeight: '40px' }}
        className={cn(
          'flex w-full items-center justify-between gap-2 rounded-md border px-3 py-1.5 text-sm',
          'transition-colors',
          'focus:outline-none focus:ring-0',
          'disabled:cursor-not-allowed disabled:opacity-50',
          dark
            ? 'bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.1)] hover:bg-[rgba(255,255,255,0.08)]'
            : 'bg-white border-slate-200 hover:bg-slate-50'
        )}
      >
        <div className='flex min-h-[24px] flex-1 flex-wrap items-center gap-1.5'>
          {selectedAllergies.length === 0 ? (
            <span className={cn('text-sm', dark ? 'text-white/40' : 'text-slate-500')}>{placeholder}</span>
          ) : (
            selectedAllergies.map((allergy: string) => (
              <Badge
                key={allergy}
                className='flex items-center gap-1 bg-brand-copper py-1 pl-2.5 pr-1 text-xs font-medium text-white hover:bg-brand-copper'
              >
                <span className='max-w-[150px] truncate'>{allergy}</span>
                <button
                  type='button'
                  onClick={e => {
                    e.stopPropagation();
                    removeAllergy(allergy);
                  }}
                  style={{ minHeight: 'auto' }}
                  className='ml-0.5 rounded-full p-0.5 transition-colors hover:bg-white/20'
                  aria-label={`Remove ${allergy}`}
                >
                  <X className='h-3 w-3' />
                </button>
              </Badge>
            ))
          )}
        </div>
        <ChevronDown className={cn('h-4 w-4 shrink-0', dark ? 'text-white/40' : 'text-slate-400')} />
      </button>

      {/* Dialog for Selection */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className='gap-0 overflow-hidden p-0 sm:max-w-[440px]'>
          {/* Header */}
          <div className='border-b border-slate-100 px-5 pb-4 pt-5'>
            <div className='flex items-center gap-3'>
              <div className='flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-brand-teal/10'>
                <AlertCircle className='h-5 w-5 text-brand-teal' />
              </div>
              <div>
                <DialogTitle className='text-base font-semibold text-slate-900'>
                  Select Allergies
                </DialogTitle>
                <DialogDescription className='mt-0.5 text-xs text-slate-500'>
                  Search and select patient allergies
                </DialogDescription>
              </div>
            </div>
          </div>

          {/* Search Input */}
          <div className='border-b border-slate-100 bg-slate-50/50 px-5 py-3'>
            <div className='relative'>
              <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400' />
              <Input
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder='Search allergies...'
                className='h-10 border-slate-200 bg-white pl-9 text-sm placeholder:text-slate-400 focus-visible:border-brand-teal focus-visible:ring-1 focus-visible:ring-brand-teal'
                autoFocus
              />
              {isLoading && (
                <div className='absolute right-3 top-1/2 -translate-y-1/2'>
                  <Loader2 className='h-4 w-4 animate-spin text-brand-teal' />
                </div>
              )}
            </div>
          </div>

          {/* Options List */}
          <div className='overflow-y-auto' style={{ maxHeight: '320px' }}>
            {isLoading && filteredOptions.length === 0 ? (
              <div className='flex flex-col items-center justify-center py-12'>
                <Loader2 className='mb-3 h-8 w-8 animate-spin text-brand-teal' />
                <span className='text-sm text-slate-500'>Loading allergies...</span>
              </div>
            ) : filteredOptions.length === 0 ? (
              <div className='flex flex-col items-center justify-center px-4 py-10'>
                <div className='mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-slate-100'>
                  <Search className='h-6 w-6 text-slate-300' />
                </div>
                <p className='text-sm font-medium text-slate-700'>
                  {searchTerm.trim().length < 2
                    ? 'Type at least 2 characters to search'
                    : 'No allergies found'}
                </p>
                {searchTerm.trim().length >= 2 && (
                  <p className='mt-1 text-xs text-slate-500'>No results for "{searchTerm}"</p>
                )}
              </div>
            ) : (
              <div className='py-1'>
                {filteredOptions.map(option => {
                  const isSelected = selectedAllergies.includes(option.value);
                  return (
                    <button
                      key={option.value}
                      type='button'
                      className={cn(
                        'flex w-full items-center gap-3 px-5 py-3 text-left transition-colors',
                        isSelected
                          ? 'border-l-2 border-brand-teal bg-brand-teal/5'
                          : 'border-l-2 border-transparent hover:bg-slate-50'
                      )}
                      onClick={() => toggleAllergy(option.value)}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleAllergy(option.value)}
                        className={cn(
                          'border-slate-300',
                          isSelected &&
                            'border-brand-teal bg-brand-teal data-[state=checked]:border-brand-teal data-[state=checked]:bg-brand-teal'
                        )}
                      />
                      <span
                        className={cn(
                          'flex-1 text-sm',
                          isSelected ? 'font-medium text-brand-teal' : 'text-slate-700'
                        )}
                      >
                        {option.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Selected Count Footer */}

          <DialogFooter className='flex items-center justify-between border-t bg-slate-50/50 px-5 py-3'>
            {selectedAllergies.length > 0 && (
              <div>
                <p className='text-sm text-slate-600'>
                  <span className='font-medium text-brand-teal'>{selectedAllergies.length}</span>{' '}
                  allergy{selectedAllergies.length !== 1 ? 'ies' : ''} selected
                </p>
              </div>
            )}
            <Button type='button' onClick={() => setOpen(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {!isControlled && form && name && form.formState.errors[name] && (
        <p className='text-[0.8rem] text-red-500'>
          {form.formState.errors[name]?.message as string}
        </p>
      )}
    </div>
  );
};

export default AllergyMultiSelect;
